import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Icon, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { adminApi } from "../api";
import { ErrorState, LoadingState } from "../components";
import type { FeedbackThreadEntry } from "../types";

const POLL_MS = 10000;

/** Renders one conversation entry: user bubbles left, support bubbles right.
 *  A user row can carry its inline support reply (legacy single-answer rows);
 *  sender='admin' rows are messages written in this chat. */
function ThreadBubbles({ entries }: { entries: FeedbackThreadEntry[] }) {
  const { t, formatDate } = useI18n();
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [entries.length]);
  return (
    <div className="admin-chat__thread">
      {entries.map((entry) => (
        <div key={entry.id} className="admin-chat__pair">
          {entry.sender === "admin" ? (
            <div className="chat-bubble chat-bubble--support admin-wrap">
              {entry.message}
              <time>{formatDate(entry.createdAt, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</time>
            </div>
          ) : (
            <>
              <div className="chat-bubble chat-bubble--user admin-wrap">
                {entry.message}
                <time>{formatDate(entry.createdAt, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</time>
              </div>
              {entry.reply && (
                <div className="chat-bubble chat-bubble--support admin-wrap">
                  {entry.reply}
                  <time>{formatDate(entry.repliedAt || entry.createdAt)}</time>
                </div>
              )}
            </>
          )}
        </div>
      ))}
      <div ref={endRef} />
      {entries.length === 0 && <p className="nlp-hint">{t("admin.feedback.threadEmpty")}</p>}
    </div>
  );
}

export function FeedbackChatPage() {
  const { t, formatNumber } = useI18n();
  const [params] = useSearchParams();
  const email = params.get("email") ?? "";
  const [entries, setEntries] = useState<FeedbackThreadEntry[] | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [live, setLive] = useState(true);
  const busy = useRef(false);

  const load = async () => {
    if (!email || busy.current) return;
    busy.current = true;
    try {
      const result = await adminApi<FeedbackThreadEntry[]>(`/feedback/thread?email=${encodeURIComponent(email)}`);
      setEntries(result);
      setStatus("ready");
      setError("");
    } catch (reason) {
      setStatus("error");
      setError(reason instanceof Error ? reason.message : t("common.error"));
    } finally {
      busy.current = false;
    }
  };

  useEffect(() => { void load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [email]);

  // Live conversation: poll while the chat is open (10 s).
  useEffect(() => {
    if (!live || !email) return;
    const timer = window.setInterval(() => { void load(); }, POLL_MS);
    return () => window.clearInterval(timer); /* eslint-disable-line react-hooks/exhaustive-deps */
  }, [live, email]);

  const send = async () => {
    const message = draft.trim();
    if (!message || sending) return;
    setSending(true);
    setSendError("");
    try {
      await adminApi("/feedback/thread", { method: "POST", body: JSON.stringify({ email, message }) });
      setDraft("");
      await load();
    } catch (reason) {
      setSendError(reason instanceof Error ? reason.message : t("admin.feedback.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  if (!email) {
    return (
      <div className="admin-page">
        <PageHeader title={t("admin.feedback.chatTitle")} />
        <ErrorState message={t("admin.feedback.threadMissingEmail")} onRetry={() => history.back()} />
      </div>
    );
  }

  return (
    <div className="admin-page admin-chat">
      <PageHeader
        title={email}
        subtitle={t("admin.feedback.chatSubtitle")}
        actions={<Link className="admin-chat__back" to="/feedback"><Icon name="arrow" size={16} />{t("admin.feedback.backToList")}</Link>}
      />

      {status === "error" && <ErrorState message={error} onRetry={() => void load()} />}
      {status === "loading" && <LoadingState rows={4} />}
      {status === "ready" && entries && (
        <>
          <section className="nl-card admin-chat__panel">
            <ThreadBubbles entries={entries} />
          </section>

          <div className="admin-chat__composer">
            <textarea
              rows={2}
              value={draft}
              placeholder={t("admin.feedback.composerPlaceholder")}
              aria-label={t("admin.feedback.composerAria")}
              onChange={(event) => { setDraft(event.target.value); setSendError(""); }}
              onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); void send(); } }}
            />
            <button
              type="button"
              className="chat-card__send"
              disabled={sending || !draft.trim()}
              aria-label={t("admin.feedback.sendAria")}
              onClick={() => void send()}
            >
              <Icon name={sending ? "timer" : "send"} size={16} />
            </button>
          </div>
          {sendError && <small className="chat-card__error" role="alert">{sendError}</small>}
          <p className="nlp-hint">
            {t("admin.feedback.liveHint")}
            <button type="button" className="admin-chat__live-toggle" aria-pressed={live} onClick={() => setLive((value) => !value)}>
              {live ? t("admin.feedback.liveOn") : t("admin.feedback.liveOff")}
            </button>
            {` · ${formatNumber(POLL_MS / 1000)} ${t("admin.feedback.seconds")}`}
          </p>
        </>
      )}
    </div>
  );
}
